import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2'; // Import de SweetAlert2
import '../styles/Admincardinaux.css';
import '../styles/AdminCardinalsList.css';

const AdminCardinalsList = () => {
  const [cardinaux, setCardinaux] = useState([]);
  const [allCardinaux, setAllCardinaux] = useState([]); // Pour conserver tous les cardinaux sans filtrage
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    paysService: '',
    paysNaissance: '',
    continent: '',
    ordre: '',
    papabile: '',
    statutVotant: ''
  });
  const [imageErrors, setImageErrors] = useState({});
  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCardinaux, setTotalCardinaux] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  // État pour désactiver la pagination
  const [showAll, setShowAll] = useState(false);

  // Image de fallback pour les cardinaux sans photo
  const defaultImage = '/images/cardinal-default.png';

  // Charger la liste des cardinaux existants
  useEffect(() => {
    const fetchAllCardinaux = async () => {
      try {
        // Charger tous les cardinaux sans pagination pour avoir l'ensemble des données
        // et pour extraire les listes de filtres
        const response = await axios.get('/api/cardinaux?limit=1000');
        setAllCardinaux(response.data.cardinaux);
        setTotalCardinaux(response.data.total);
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement des cardinaux:', err);
        setErrorMessage('Erreur lors du chargement des cardinaux');
        setLoading(false);
      }
    };
    
    fetchAllCardinaux();
  }, []);

  // Effet pour la pagination et le filtrage
  useEffect(() => {
    // Appliquer les filtres sur allCardinaux
    let filteredResults = allCardinaux;
    
    // Filtrer par terme de recherche
    if (searchTerm) {
      const searchTermLower = searchTerm.toLowerCase();
      filteredResults = filteredResults.filter(cardinal => 
        cardinal.nomComplet?.toLowerCase().includes(searchTermLower) ||
        cardinal.nom?.toLowerCase().includes(searchTermLower) ||
        cardinal.prenom?.toLowerCase().includes(searchTermLower) ||
        cardinal.titre?.toLowerCase().includes(searchTermLower)
      );
    }
    
    // Filtrer par pays de service
    if (filters.paysService) {
      filteredResults = filteredResults.filter(cardinal => 
        cardinal.paysService && cardinal.paysService.toLowerCase() === filters.paysService.toLowerCase()
      );
    }
    
    // Filtrer par pays de naissance
    if (filters.paysNaissance) {
      filteredResults = filteredResults.filter(cardinal => 
        cardinal.paysNaissance && cardinal.paysNaissance.toLowerCase() === filters.paysNaissance.toLowerCase()
      );
    }
    
    // Filtrer par continent
    if (filters.continent) {
      filteredResults = filteredResults.filter(cardinal => 
        cardinal.continent === filters.continent
      );
    }
    
    // Filtrer par ordre
    if (filters.ordre) {
      filteredResults = filteredResults.filter(cardinal => 
        cardinal.ordre === filters.ordre
      );
    }
    
    // Filtrer par statut papabile
    if (filters.papabile !== '') {
      const isPapabile = filters.papabile === 'true';
      filteredResults = filteredResults.filter(cardinal => 
        cardinal.papabile === isPapabile
      );
    }
    
    // Filtrer par statut votant
    if (filters.statutVotant) {
      filteredResults = filteredResults.filter(cardinal => 
        cardinal.statutVotant === filters.statutVotant
      );
    }
    
    // Calculer le nombre total de pages
    const calculatedTotalPages = Math.ceil(filteredResults.length / itemsPerPage);
    setTotalPages(calculatedTotalPages);
    setTotalCardinaux(filteredResults.length);
    
    // Si showAll est activé, afficher tous les cardinaux sans pagination
    if (showAll) {
      setCardinaux(filteredResults);
    } else {
      // Pagination des résultats
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedResults = filteredResults.slice(startIndex, endIndex);
      setCardinaux(paginatedResults);
    }
    
  }, [allCardinaux, searchTerm, filters, currentPage, itemsPerPage, showAll]);

  // Extraire tous les pays de service uniques en tenant compte de la casse
  const getUniquePaysService = () => {
    const paysSet = new Set();
    allCardinaux.forEach(cardinal => {
      if (cardinal.paysService) {
        paysSet.add(cardinal.paysService);
      }
    });
    return Array.from(paysSet).sort();
  };

  // Extraire tous les pays de naissance uniques
  const getUniquePaysNaissance = () => {
    const paysSet = new Set();
    allCardinaux.forEach(cardinal => {
      if (cardinal.paysNaissance) {
        paysSet.add(cardinal.paysNaissance);
      }
    });
    return Array.from(paysSet).sort();
  };

  // Extraire tous les ordres uniques
  const getUniqueOrdres = () => {
    const ordresSet = new Set();
    allCardinaux.forEach(cardinal => {
      if (cardinal.ordre) {
        ordresSet.add(cardinal.ordre);
      }
    });
    return Array.from(ordresSet).sort();
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Revenir à la première page lors d'une nouvelle recherche
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setCurrentPage(1); // Revenir à la première page lors d'un changement de filtre
  };

  // Gérer le changement d'état "Voir tous"
  const handleShowAllChange = (e) => {
    setShowAll(e.target.checked);
  };

  // Gérer le changement de statut papabile
  const togglePapabile = async (id, currentStatus, nom) => {
    try {
      // Mettre à jour le statut papabile dans la base de données
      await axios.put(`/api/cardinaux/${id}`, {
        papabile: !currentStatus
      });
      
      // Mettre à jour l'état local
      setAllCardinaux(allCardinaux.map(cardinal => 
        cardinal._id === id ? { ...cardinal, papabile: !currentStatus } : cardinal
      ));
      
      // Afficher un message de succès
      const statusMessage = !currentStatus ? 'marqué comme papabile' : 'retiré des papabili';
      Swal.fire({
        title: 'Statut mis à jour',
        text: `Le cardinal ${nom} a été ${statusMessage} avec succès.`,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (err) {
      Swal.fire({
        title: 'Erreur',
        text: `Erreur lors de la mise à jour du statut: ${err.response?.data?.msg || err.message}`,
        icon: 'error'
      });
    }
  };

  // Pagination - Aller à la page précédente
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Pagination - Aller à la page suivante
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Fonction pour gérer la suppression d'un cardinal avec SweetAlert
  const handleDelete = async (id, nom) => {
    // Utiliser SweetAlert2 pour la confirmation
    Swal.fire({
      title: 'Êtes-vous sûr?',
      text: `Vous êtes sur le point de supprimer le cardinal ${nom}. Cette action est irréversible!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#8c1515',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer!',
      cancelButtonText: 'Annuler'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`/api/cardinaux/${id}`);
          
          // Mettre à jour la liste
          setAllCardinaux(allCardinaux.filter(cardinal => cardinal._id !== id));
          
          // Afficher un message de succès avec SweetAlert
          Swal.fire(
            'Supprimé!',
            `Le cardinal ${nom} a été supprimé avec succès.`,
            'success'
          );
        } catch (err) {
          // Afficher un message d'erreur avec SweetAlert
          Swal.fire(
            'Erreur',
            `Erreur lors de la suppression: ${err.response?.data?.msg || err.message}`,
            'error'
          );
        }
      }
    });
  };

  // Utiliser la route API pour les images avec le bon content-type
  const getApiImageUrl = (originalUrl, cardinalId) => {
    if (!originalUrl || imageErrors[cardinalId]) return defaultImage;
    
    // Extraire le nom du fichier de l'URL
    const filename = originalUrl.split('/').pop().split('?')[0];
    
    // Ajouter un timestamp pour éviter le cache du navigateur
    const timestamp = new Date().getTime();
    return `/api/images/${filename}?t=${timestamp}`;
  };

  // Gestionnaire d'erreur pour les images
  const handleImageError = (cardinalId) => {
    console.log(`Erreur de chargement d'image pour cardinal ${cardinalId}, utilisation de l'image par défaut`);
    setImageErrors(prev => ({
      ...prev,
      [cardinalId]: true
    }));
  };

  // Réinitialiser les filtres
  const resetFilters = () => {
    setSearchTerm('');
    setFilters({
      paysService: '',
      paysNaissance: '',
      continent: '',
      ordre: '',
      papabile: '',
      statutVotant: ''
    });
    setCurrentPage(1);
    setShowAll(false);
  };

  // Listes des valeurs uniques pour les filtres
  const uniquePaysService = getUniquePaysService();
  const uniquePaysNaissance = getUniquePaysNaissance();
  const uniqueOrdres = getUniqueOrdres();
  const continents = ['Afrique', 'Amérique', 'Asie', 'Europe', 'Océanie'];

  return (
    <div className="admin-page">
      <h1>Administration des Cardinaux</h1>
      
      {message && <div className="message success">{message}</div>}
      {errorMessage && <div className="message error">{errorMessage}</div>}
      
      <div className="admin-tabs">
        <div className="tab-header">
          <Link to="/admin/cardinaux" className="tab-button">Ajouter un cardinal</Link>
          <button className="tab-button active">Liste des cardinaux</button>
        </div>
        
        <div className="list-filters">
          <div className="search-container">
            <input
              type="text"
              placeholder="Rechercher un cardinal (nom, prénom, titre)..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>
          
          <div className="filter-container">
            <select 
              name="paysService" 
              value={filters.paysService} 
              onChange={handleFilterChange}
              className="filter-select"
            >
              <option value="">Tous les pays de service</option>
              {uniquePaysService.map(pays => (
                <option key={pays} value={pays}>{pays}</option>
              ))}
            </select>
            
            <select 
              name="paysNaissance" 
              value={filters.paysNaissance} 
              onChange={handleFilterChange}
              className="filter-select"
            >
              <option value="">Tous les pays de naissance</option>
              {uniquePaysNaissance.map(pays => (
                <option key={pays} value={pays}>{pays}</option>
              ))}
            </select>
            
            <select
              name="continent"
              value={filters.continent}
              onChange={handleFilterChange}
              className="filter-select"
            >
              <option value="">Tous les continents</option>
              {continents.map(continent => (
                <option key={continent} value={continent}>{continent}</option>
              ))}
            </select>
            
            <select
              name="ordre"
              value={filters.ordre}
              onChange={handleFilterChange}
              className="filter-select"
            >
              <option value="">Tous les ordres</option>
              {uniqueOrdres.map(ordre => (
                <option key={ordre} value={ordre}>{ordre}</option>
              ))}
            </select>
            
            <select
              name="statutVotant"
              value={filters.statutVotant}
              onChange={handleFilterChange}
              className="filter-select"
            >
              <option value="">Tous les statuts</option>
              <option value="OUI">Électeurs uniquement</option>
              <option value="NON">Non-électeurs uniquement</option>
            </select>
            
            <select
              name="papabile"
              value={filters.papabile}
              onChange={handleFilterChange}
              className="filter-select"
            >
              <option value="">Tous les cardinaux</option>
              <option value="true">Papabile uniquement</option>
              <option value="false">Non papabile uniquement</option>
            </select>

            <div className="show-all-container">
              <label className="checkbox-filter">
                <input 
                  type="checkbox"
                  checked={showAll}
                  onChange={handleShowAllChange}
                />
                Voir tous
              </label>
            </div>

            <button
              className="reset-filters-button"
              onClick={resetFilters}
              title="Réinitialiser tous les filtres"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      </div>
      
      <div className="admin-list">
        <h2>Liste des cardinaux</h2>
        {loading ? (
          <p>Chargement...</p>
        ) : cardinaux.length === 0 ? (
          searchTerm || Object.values(filters).some(val => val !== '') ? 
            <p>Aucun cardinal ne correspond à vos critères de recherche</p> : 
            <p>Aucun cardinal enregistré</p>
        ) : (
          <>
            <table className="cardinaux-table">
              <thead>
                <tr>
                  <th>Photo</th>
                  <th>Nom</th>
                  <th>Ordre</th>
                  <th>Pays de service</th>
                  <th>Pays de naissance</th>
                  <th>Continent</th>
                  <th>Âge</th>
                  <th>Statut</th>
                  <th>Papabile</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {cardinaux.map(cardinal => (
                  <tr key={cardinal._id}>
                    <td>
                      <img 
                        src={getApiImageUrl(cardinal.photo, cardinal._id)} 
                        alt={cardinal.nomComplet} 
                        className="cardinal-mini-photo" 
                        onError={() => handleImageError(cardinal._id)}
                      />
                    </td>
                    <td>{cardinal.nomComplet}</td>
                    <td>{cardinal.ordre || "Non défini"}</td>
                    <td>{cardinal.paysService}</td>
                    <td>{cardinal.paysNaissance}</td>
                    <td>{cardinal.continent || "Non défini"}</td>
                    <td>{cardinal.age}</td>
                    <td>{cardinal.statutVotant === 'OUI' ? 'Électeur' : 'Non-électeur'}</td>
                    <td>
                      <label className="papabile-toggle">
                        <input
                          type="checkbox"
                          checked={cardinal.papabile}
                          onChange={() => togglePapabile(cardinal._id, cardinal.papabile, cardinal.nomComplet)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </td>
                    <td>
                      <div className="table-actions">
                        <Link to={`/admin/cardinaux/edit/${cardinal._id}`} className="edit-button">Éditer</Link>
                        <button 
                          onClick={() => handleDelete(cardinal._id, cardinal.nomComplet)} 
                          className="delete-button"
                        >
                          Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Composant de pagination - affiché seulement si showAll est false */}
            {!showAll && (
              <div className="pagination">
                <button
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                  className="pagination-button"
                >
                  &laquo; Précédent
                </button>
                
                <span className="pagination-info">
                  Page {currentPage} sur {totalPages}
                </span>
                
                <button
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                  className="pagination-button"
                >
                  Suivant &raquo;
                </button>
              </div>
            )}
            
            <div className="results-count">
              Affichage de {cardinaux.length} cardinal(aux) sur {totalCardinaux} au total
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminCardinalsList;